import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  UserPlus, Pencil, Trash2, KeyRound, LinkIcon, Plus, X,
  ShieldCheck, Calculator, PieChart, Users as UsersIcon,
  AlertTriangle, Eraser,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  admin: { label: "Admin", desc: "Full access — everything", icon: ShieldCheck, cls: "bg-red-50 text-red-700 border-red-200" },
  accounts: { label: "Accountant", desc: "Enter data, no approvals", icon: Calculator, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  partner: { label: "Partner", desc: "View — sees their LLP share", icon: PieChart, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  investor: { label: "Investor", desc: "External — sees only assigned investments", icon: UsersIcon, cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "investor", partner_id: "", whatsapp_phone: "" });
  const [assignDlg, setAssignDlg] = useState(null); // user object
  const [assignments, setAssignments] = useState([]);
  const [assignForm, setAssignForm] = useState({ investment_id: "", share_percent: "" });
  const [eraseDlg, setEraseDlg] = useState(false);
  const [eraseConfirm, setEraseConfirm] = useState("");
  const [eraseIncInv, setEraseIncInv] = useState(false);
  const [eraseIncPar, setEraseIncPar] = useState(false);
  const [erasing, setErasing] = useState(false);

  const isAdmin = me?.role === "admin";

  const load = async () => {
    const [u, i, p] = await Promise.all([
      api.get("/auth/users"),
      api.get("/investments"),
      api.get("/partners"),
    ]);
    setUsers(u.data); setInvestments(i.data); setPartners(p.data);
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const openAdd = () => {
    setEditing(null);
    setForm({ email: "", name: "", password: "", role: "investor", partner_id: "", whatsapp_phone: "" });
    setDlg(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name || "", password: "", role: u.role, partner_id: u.partner_id || "", whatsapp_phone: u.whatsapp_phone || "" });
    setDlg(true);
  };
  const save = async () => {
    if (!form.email || !form.name) return toast.error("Name and email required");
    try {
      if (editing) {
        const patch = { name: form.name, role: form.role, whatsapp_phone: form.whatsapp_phone };
        if (form.password) patch.password = form.password;
        await api.put(`/auth/users/${editing.id}`, patch);
        toast.success("User updated");
      } else {
        if (!form.password) return toast.error("Password required");
        await api.post("/auth/register", {
          email: form.email, name: form.name, password: form.password, role: form.role,
          whatsapp_phone: form.whatsapp_phone,
        });
        toast.success("User created");
      }
      setDlg(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (u) => {
    if (u.id === me.id) return toast.error("Cannot delete yourself");
    if (!window.confirm(`Delete user "${u.email}"? Their assignments will also be removed.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("User deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openAssign = async (u) => {
    setAssignDlg(u);
    setAssignForm({ investment_id: "", share_percent: "" });
    try {
      const r = await api.get(`/auth/users/${u.id}/assignments`);
      setAssignments(r.data);
    } catch { setAssignments([]); }
  };
  const addAssignment = async () => {
    if (!assignForm.investment_id) return toast.error("Pick an investment");
    try {
      await api.post(`/auth/users/${assignDlg.id}/assignments`, {
        investment_id: assignForm.investment_id,
        share_percent: Number(assignForm.share_percent || 0),
      });
      const r = await api.get(`/auth/users/${assignDlg.id}/assignments`);
      setAssignments(r.data);
      setAssignForm({ investment_id: "", share_percent: "" });
      toast.success("Assignment saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeAssignment = async (aid) => {
    try {
      await api.delete(`/auth/users/${assignDlg.id}/assignments/${aid}`);
      setAssignments(assignments.filter((a) => a.id !== aid));
    } catch { toast.error("Failed"); }
  };

  const runErase = async () => {
    if (eraseConfirm !== "ERASE") return;
    setErasing(true);
    try {
      const r = await api.post("/admin/erase-transactions", {
        confirm: "ERASE",
        include_investments: eraseIncInv,
        include_partners: eraseIncPar,
      });
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Erased ${total} records across ${Object.keys(r.data.deleted).length} collections`);
      setEraseDlg(false);
      setEraseConfirm("");
      setEraseIncInv(false);
      setEraseIncPar(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setErasing(false); }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-slate-500">
        <div className="text-sm">Only admins can manage users.</div>
      </div>
    );
  }

  const totalAssignedShare = assignments.reduce((a, x) => a + Number(x.share_percent || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Access · Roles · Assignments</div>
          <h1 className="mt-2 font-editorial font-light text-[34px] leading-[1.02] sm:text-4xl md:text-5xl text-slate-900 tracking-[-0.035em]" data-testid="users-title">
            <em className="italic">Users.</em>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-lg">
            Add teammates and external investors. Investors only see the investments you assign to them.
          </p>
        </div>
        <Button onClick={openAdd} className="rounded-md bg-red-600 hover:bg-red-700 text-white h-9 gap-1.5" data-testid="add-user">
          <UserPlus className="h-4 w-4" /> New user
        </Button>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(ROLE_META).map(([key, m]) => (
          <div key={key} className={`border rounded-md p-3 ${m.cls}`}>
            <div className="flex items-center gap-2">
              <m.icon className="h-4 w-4" />
              <div className="text-[12px] font-semibold">{m.label}</div>
            </div>
            <div className="text-[10px] mt-1 opacity-80">{m.desc}</div>
          </div>
        ))}
      </div>

      {/* Users table */}
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Name</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Email</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Role</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Assignments</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const rm = ROLE_META[u.role] || ROLE_META.investor;
                return (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`user-${u.email}`}>
                    <td className="p-3 font-medium text-slate-900">{u.name}</td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded border ${rm.cls}`}>
                        <rm.icon className="h-3 w-3" /> {rm.label}
                      </span>
                    </td>
                    <td className="p-3">
                      {(u.role === "investor" || u.role === "accounts") ? (
                        <button onClick={() => openAssign(u)} className="text-[11px] text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`assign-${u.email}`}>
                          <LinkIcon className="h-3 w-3" /> Manage
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-300">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(u)} className="p-1.5 rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100" data-testid={`edit-${u.email}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {u.id !== me.id && (
                          <button onClick={() => remove(u)} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`del-${u.email}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-10 text-center text-slate-400 text-[12px]">
                    No users yet — click &ldquo;New user&rdquo; to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-slate-100">
          {users.length === 0 && (
            <li className="p-8 text-center text-slate-400 text-[13px]">No users yet.</li>
          )}
          {users.map((u) => {
            const rm = ROLE_META[u.role] || ROLE_META.investor;
            return (
              <li key={u.id} className="p-3.5" data-testid={`user-mrow-${u.email}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{u.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[9.5px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${rm.cls}`}>
                    <rm.icon className="h-3 w-3" /> {rm.label}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  {(u.role === "investor" || u.role === "accounts") && (
                    <button onClick={() => openAssign(u)}
                      className="inline-flex items-center gap-1 text-[11px] border border-red-200 rounded-md px-2 py-1 text-red-700 bg-red-50 active:bg-red-100"
                      data-testid={`assign-mobile-${u.email}`}>
                      <LinkIcon className="h-3.5 w-3.5" /> Assignments
                    </button>
                  )}
                  <button onClick={() => openEdit(u)}
                    className="inline-flex items-center gap-1 text-[11px] border border-slate-200 rounded-md px-2 py-1 text-slate-700 active:bg-slate-100 ml-auto">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  {u.id !== me.id && (
                    <button onClick={() => remove(u)}
                      className="inline-flex items-center gap-1 text-[11px] border border-slate-200 rounded-md px-2 py-1 text-slate-500 hover:text-red-600 active:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Danger Zone — erase all transactions */}
      <div className="border border-red-200 bg-red-50/40 rounded-md p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <div className="text-[13px] font-semibold text-red-800">Danger Zone</div>
              <div className="text-[11px] text-red-700/80 mt-0.5 max-w-lg leading-snug">
                Permanently erase every transaction — Cash Book entries, Bills, Invoices, Loan payments, Cafe P&amp;L,
                valuations and imported statements. <strong>Master data is preserved</strong> (users, partners,
                investments, accounts, categories, vendors).
              </div>
            </div>
          </div>
          <Button
            onClick={() => { setEraseConfirm(""); setEraseDlg(true); }}
            variant="outline"
            className="rounded-md border-red-300 bg-white text-red-700 hover:bg-red-50 hover:text-red-800 h-9 gap-1.5"
            data-testid="erase-open"
          >
            <Eraser className="h-4 w-4" /> Erase all transactions
          </Button>
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" disabled={!!editing} data-testid="user-email" />
              {editing && <div className="text-[10px] text-slate-400 mt-1">Email cannot be changed</div>}
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_META).map(([key, m]) => (
                    <SelectItem key={key} value={key}>{m.label} — <span className="text-slate-500">{m.desc}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{editing ? "Reset password (optional)" : "Password"}</Label>
              <div className="relative mt-1.5">
                <KeyRound className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Leave blank to keep unchanged" : "Min 6 chars"} className="pl-8" data-testid="user-password" />
              </div>
            </div>
            <div>
              <Label>WhatsApp phone <span className="text-slate-400 text-[10px]">(E.164 — used for reminder pings)</span></Label>
              <Input value={form.whatsapp_phone} onChange={(e) => setForm({ ...form, whatsapp_phone: e.target.value })}
                placeholder="+919876543210" className="mt-1.5 font-mono-fin" data-testid="user-whatsapp" />
            </div>
            {form.role === "investor" && !editing && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                After creating this investor, use <b>Assignments → Manage</b> to link the investments they can see.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md h-9">Cancel</Button>
            <Button onClick={save} className="rounded-md h-9 bg-red-600 hover:bg-red-700 text-white" data-testid="user-save">
              {editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment dialog */}
      <Dialog open={!!assignDlg} onOpenChange={(o) => !o && setAssignDlg(null)}>
        <DialogContent className="max-w-xl" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              {assignDlg?.role === "accounts" ? "Project scope for " : "Investments for "}
              <span className="font-normal text-slate-500">{assignDlg?.name}</span>
            </DialogTitle>
          </DialogHeader>

          {assignDlg?.role === "accounts" && (
            <div className="border border-blue-200 bg-blue-50/60 rounded-md p-2.5 text-[11px] text-blue-800 leading-relaxed">
              This accountant will <b>only</b> see Cash/Bank, Bills, Insights and Reports for the projects listed below. Leave empty to grant full company-wide access.
              <span className="block text-blue-600 mt-1">Share % is ignored for accountants.</span>
            </div>
          )}

          <div className="border border-slate-200 rounded-md bg-slate-50/40 p-3 space-y-2">
            <div className="text-[11px] font-medium text-slate-600 uppercase tracking-wider">Add investment</div>
            <div className="grid grid-cols-[1fr_120px_auto] gap-2 items-end">
              <div>
                <Label className="text-[11px]">Investment</Label>
                <Select value={assignForm.investment_id} onValueChange={(v) => setAssignForm({ ...assignForm, investment_id: v })}>
                  <SelectTrigger className="h-9 mt-1" data-testid="assign-inv">
                    <SelectValue placeholder="Pick one…" />
                  </SelectTrigger>
                  <SelectContent>
                    {investments.map((i) => (
                      <SelectItem key={i.id} value={i.id}>{i.name} <span className="text-slate-400">· {i.type}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">% share</Label>
                <Input type="number" step="0.01" value={assignForm.share_percent} onChange={(e) => setAssignForm({ ...assignForm, share_percent: e.target.value })} placeholder="0.00" className="h-9 mt-1 font-mono-fin text-right" data-testid="assign-share" />
              </div>
              <Button onClick={addAssignment} className="h-9 rounded-md bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="assign-add">
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-2.5 text-left text-[10px] tracking-wider uppercase text-slate-500">Investment</th>
                  <th className="p-2.5 text-left text-[10px] tracking-wider uppercase text-slate-500">Type</th>
                  <th className="p-2.5 text-right text-[10px] tracking-wider uppercase text-slate-500">Share %</th>
                  <th className="p-2.5 text-right text-[10px] tracking-wider uppercase text-slate-500"></th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100" data-testid={`asgn-${a.id}`}>
                    <td className="p-2.5 font-medium text-slate-900">{a.investment_name}</td>
                    <td className="p-2.5 text-slate-500 uppercase text-[10px] tracking-wider">{a.investment_type}</td>
                    <td className="p-2.5 text-right font-mono-fin">{Number(a.share_percent).toFixed(2)}%</td>
                    <td className="p-2.5 text-right">
                      <button onClick={() => removeAssignment(a.id)} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {assignments.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-slate-400 text-[12px]">
                      No investments assigned yet.
                    </td>
                  </tr>
                )}
              </tbody>
              {assignments.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-50 border-t border-slate-200">
                    <td colSpan={2} className="p-2.5 text-[11px] uppercase tracking-wider text-slate-500 font-medium">Total assigned</td>
                    <td className="p-2.5 text-right font-mono-fin font-semibold">{totalAssignedShare.toFixed(2)}%</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDlg(null)} className="rounded-md h-9">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Erase confirmation dialog */}
      <Dialog open={eraseDlg} onOpenChange={setEraseDlg}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Erase all transactions
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[12.5px] text-slate-700 leading-relaxed">
              This will permanently delete every record in the following collections:
            </div>
            <ul className="text-[11px] text-slate-600 font-mono-fin bg-slate-50 border border-slate-200 rounded-md p-3 space-y-0.5">
              <li>· transactions <span className="text-slate-400">(cash / bank / loan payments / cafe share)</span></li>
              <li>· bills <span className="text-slate-400">(purchase bills + sales invoices)</span></li>
              <li>· cafe_pnl</li>
              <li>· valuations</li>
              <li>· distributions</li>
              <li>· statement_imports</li>
              <li>· documents</li>
              {eraseIncInv && (
                <li className="text-red-600">· investments <span className="text-red-400">(+ investor_assignments)</span></li>
              )}
              {eraseIncPar && (
                <li className="text-red-600">· partners</li>
              )}
            </ul>

            {/* Optional includes */}
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2">
              <div className="text-[11px] font-semibold text-amber-900 uppercase tracking-wider">Also delete (optional)</div>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={eraseIncInv}
                  onCheckedChange={(v) => setEraseIncInv(!!v)}
                  className="mt-0.5"
                  data-testid="erase-inc-inv"
                />
                <div className="text-[12px] leading-snug">
                  <div className="font-medium text-amber-900">Investments</div>
                  <div className="text-[10px] text-amber-800/80">Also wipes investor assignments referencing them.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={eraseIncPar}
                  onCheckedChange={(v) => setEraseIncPar(!!v)}
                  className="mt-0.5"
                  data-testid="erase-inc-par"
                />
                <div className="text-[12px] leading-snug">
                  <div className="font-medium text-amber-900">Partners</div>
                  <div className="text-[10px] text-amber-800/80">The 7 LLP partner records will be deleted.</div>
                </div>
              </label>
            </div>

            <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2.5">
              <strong>Always preserved:</strong> users, accounts, categories, sub-categories, vendors/customers
              {!eraseIncInv && ", investments"}{!eraseIncPar && ", partners"}
              {(!eraseIncInv || !eraseIncPar) ? "." : "."}
            </div>

            <div>
              <Label className="text-[11px]">Type <span className="font-mono-fin font-bold text-red-600">ERASE</span> to confirm</Label>
              <Input
                value={eraseConfirm}
                onChange={(e) => setEraseConfirm(e.target.value)}
                placeholder="ERASE"
                className="mt-1.5 font-mono-fin"
                data-testid="erase-confirm"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEraseDlg(false)} className="rounded-md h-9" disabled={erasing}>Cancel</Button>
            <Button
              onClick={runErase}
              disabled={eraseConfirm !== "ERASE" || erasing}
              className="rounded-md h-9 bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              data-testid="erase-confirm-btn"
            >
              {erasing ? "Erasing…" : "Erase everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
